import { useState, useCallback } from 'react';
import { launchCamera } from 'react-native-image-picker';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { FileMessageCreateParams } from '@sendbird/chat/message';
import { ToastNotifications } from 'app/services/inAppToastNotifications';
import { TRootStackParamList } from 'app/types/navigation';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { requestCameraPermission } from '@shared/utils/cameraPermission';

type NavigationProp = NativeStackNavigationProp<TRootStackParamList>;

export const useChatUpload = (
  pathwayId: string,
  onPressSendFileMessage: ((params: FileMessageCreateParams) => Promise<void>) | undefined,
) => {
  const navigation = useNavigation<NavigationProp>();
  const { t } = dynamicTranslations();

  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleCameraUpload = useCallback(async () => {
    setIsUploadModalVisible(false);

    await new Promise<void>((resolve) => setTimeout(resolve, 350));

    const hasPermission = await requestCameraPermission(
      t('history.documents.patientSection.camera.permissionTitle'),
      t('history.documents.patientSection.camera.permissionMessage'),
      t('history.documents.patientSection.camera.permissionCancel'),
      t('history.documents.patientSection.camera.permissionSettings'),
    );
    if (!hasPermission || !onPressSendFileMessage) return;

    const result = await launchCamera({ mediaType: 'photo', quality: 0.8 });
    if (result.didCancel || result.errorCode) return;

    const asset = result.assets?.[0];
    if (!asset?.uri) return;

    try {
      setIsUploading(true);
      await onPressSendFileMessage({
        file: {
          uri: asset.uri,
          name: asset.fileName ?? 'photo.jpg',
          type: asset.type ?? 'image/jpeg',
        },
        fileName: asset.fileName ?? 'photo.jpg',
        mimeType: asset.type ?? 'image/jpeg',
        fileSize: asset.fileSize,
      });
    } catch {
      ToastNotifications.displayErrorNotification(t('screens.chat.upload.error'));
    } finally {
      setIsUploading(false);
    }
  }, [onPressSendFileMessage, t]);

  const handleExternalDeviceUpload = useCallback(() => {
    setIsUploadModalVisible(false);
    navigation.navigate('DocumentUploadQRPage', { pathwayId });
  }, [navigation, pathwayId]);

  return {
    isUploadModalVisible,
    setIsUploadModalVisible,
    isUploading,
    handleCameraUpload,
    handleExternalDeviceUpload,
  };
};
